import { useNavigate, useParams } from "react-router-dom";
import '../App.css';
import { useState, useEffect } from 'react';
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import supabase from '../supabase';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import LoadingScreen from '../components/LoadingScreen';
import { MAINTENANCE } from '../config';
import { listB2Files } from '../utils/uploadToB2';

// Refonte : Inter / arrondis / ombres → Helvetica Neue épaisse, plat, carré,
// noir/blanc. Boutons en texte nu (.wlink) au lieu de pilules bordées.
const font = "'Helvetica Neue', Arial, sans-serif";
const INK = '#111';
const MUTED = '#8a8a86';
const BORDER = '#e5e0d5';

function Album() {
    const navigate = useNavigate();
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);
    const [coverLightbox, setCoverLightbox] = useState(false);
    const { albumName } = useParams(); const [currentAlbum, setCurrentAlbum] = useState(null);
    const [photos, setPhotos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creatorAvatar, setCreatorAvatar] = useState('');
    const [creatorUsername, setCreatorUsername] = useState('');
    const [coAuthorAvatars, setCoAuthorAvatars] = useState([]);
    const [moreFromCreator, setMoreFromCreator] = useState([]);
    const [copied, setCopied] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 900);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 900);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (!currentAlbum) return;

        const authorName = currentAlbum.created_by || '';
        const pageTitle = authorName
            ? `${currentAlbum.title} — ${authorName} | winston.ie`
            : `${currentAlbum.title} | winston.ie`;

        document.title = pageTitle;

        const setMeta = (property, content) => {
            if (!content) return;
            let tag = document.querySelector(`meta[property="${property}"]`);
            if (!tag) {
                tag = document.createElement('meta');
                tag.setAttribute('property', property);
                document.head.appendChild(tag);
            }
            tag.setAttribute('content', content);
        };

        setMeta('og:title', pageTitle);
        setMeta('og:description', currentAlbum.description || currentAlbum.location || 'Une archive photographique sur winston.ie.');

        if (currentAlbum.cover_url) {
            const isAbsolute = currentAlbum.cover_url.startsWith('http');
            setMeta('og:image', isAbsolute ? currentAlbum.cover_url : `${window.location.origin}${currentAlbum.cover_url}`);
        }

        setMeta('og:type', 'article');
        setMeta('og:url', window.location.href);

        // Nettoyage — remet un titre/image neutres en quittant la page,
        // pour ne pas laisser le titre de cet album "collé" sur une autre page.
        return () => {
            document.title = 'winston.ie';
        };
    }, [currentAlbum]);

    useEffect(() => {
        const fetchAlbum = async () => {
            const { data } = await supabase
                .from('albums')
                .select('*')
                .eq('folder', albumName)
                .single();

            if (data) {
                setCurrentAlbum(data);

                if (data.created_by) {
                    const fullName = data.created_by?.trim();
                    const { data: usersData } = await supabase
                        .from('users')
                        .select('avatar_url, first_name, last_name, username');
                    if (usersData) {
                        const foundUser = usersData.find(u =>
                            `${u.first_name || ''} ${u.last_name || ''}`.trim() === fullName
                        );
                        if (foundUser?.avatar_url) setCreatorAvatar(foundUser.avatar_url);
                        if (foundUser?.username) setCreatorUsername(foundUser.username);

                        if (data.co_authors?.length > 0) {
                            const coAvatars = data.co_authors
                                .map(name => usersData.find(u => `${u.first_name || ''} ${u.last_name || ''}`.trim() === name.trim()))
                                .filter(Boolean);
                            setCoAuthorAvatars(coAvatars);
                        }
                    }
                }

                if (data.created_by) {
                    const { data: others } = await supabase
                        .from('albums')
                        .select('*')
                        .eq('created_by', data.created_by)
                        .eq('archived', false)
                        .neq('folder', data.folder)
                        .limit(4);
                    if (others) setMoreFromCreator(others);
                }

                try {
                    const files = await listB2Files(`photos/${data.region}/${data.folder}`);
                    setPhotos(files.map(f => f.url));
                } catch (err) {
                    console.error('Erreur listage photos B2:', err);
                }
            }
            setLoading(false);
        };
        fetchAlbum();
    }, [albumName]);

    const handleCopyLink = () => {
        navigator.clipboard.writeText(window.location.href);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    if (loading) return <LoadingScreen variant="light" />;
    if (!currentAlbum) return <p>Album not found.</p>;

    if (MAINTENANCE) return (
        <div style={{ position: 'relative', minHeight: '100vh', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: font }}>
            <video autoPlay loop muted playsInline style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: -1 }}>
                <source src="/video/background.mp4" type="video/mp4" />
            </video>
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: -1 }} />
            <div style={{ textAlign: 'center' }}>
                <p style={{ fontSize: '11px', letterSpacing: '0.15em', textTransform: 'uppercase', fontWeight: 500, color: 'rgba(255,255,255,0.4)', marginBottom: '12px' }}>winston.ie</p>
                <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)', fontWeight: '500', letterSpacing: '0.02em' }}>Quietly reorganizing. Back soon.</p>
            </div>
        </div>
    );

    return (
        <div style={{ background: '#fafaf8', minHeight: '100vh', fontFamily: font, display: 'flex', flexDirection: 'column' }}>
            <style>{`
                .wlink { position: relative; display: inline-block; cursor: pointer; opacity: 0.88; transition: opacity .25s ease; }
                .wlink:hover { opacity: 1; }
                .wlink::after { content: ''; position: absolute; left: 0; right: 0; bottom: -3px; height: 1px; background: currentColor; transform: scaleX(0); transform-origin: left; transition: transform .35s cubic-bezier(.4,0,.2,1); }
                .wlink:hover::after { transform: scaleX(1); }
                .album-layout { display: flex; align-items: flex-start; gap: 56px; max-width: 1320px; margin: 0 auto; padding: 32px 32px 0; }
                .album-sidebar { width: 300px; flex-shrink: 0; position: sticky; top: 32px; }
                .album-content { flex: 1; min-width: 0; }
                @media (max-width: 900px) {
                    .album-layout { flex-direction: column; gap: 32px; padding: 20px 20px 0; }
                    .album-sidebar { width: 100%; position: static; }
                }
            `}</style>

            <Navbar variant="light" />

            <div style={{ flex: 1 }}>

                <div className="album-layout">

                    {/* SIDEBAR — infos fixes, cover discrète */}
                    <div className="album-sidebar">
                        <p
                            onClick={() => navigate('/chroniques')} className="wlink" style={{ fontFamily: font, fontWeight: 500, fontSize: '12px', color: MUTED, cursor: 'pointer', margin: '0 0 24px' }}
                        >
                            ← Back
                        </p>

                        <div
                            onClick={() => setCoverLightbox(true)}
                            style={{
                                width: '100%',
                                aspectRatio: '1 / 1',
                                overflow: 'hidden',
                                cursor: 'zoom-in',
                                background: '#ece8df',
                                marginBottom: '24px',
                            }}
                        >
                            {currentAlbum.cover_url && (
                                <img src={currentAlbum.cover_url} alt="cover" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                            )}
                        </div>

                        {currentAlbum.campaign_year && (
                            <p
                                onClick={() => navigate(`/campaign/${currentAlbum.campaign_year}`)}
                                className="wlink"
                                style={{
                                    display: 'inline-block',
                                    fontFamily: font, fontWeight: 700,
                                    fontSize: '10px', letterSpacing: '0.08em', textTransform: 'uppercase',
                                    color: INK, cursor: 'pointer', margin: '0 0 16px',
                                }}
                            >
                                Campaign {currentAlbum.campaign_year}
                            </p>
                        )}

                        <h1 style={{
                            fontFamily: font, fontWeight: 700, fontSize: '22px',
                            color: INK, margin: '0 0 8px', letterSpacing: '-0.01em', lineHeight: 1.3,
                        }}>
                            {currentAlbum.title}
                        </h1>

                        <p style={{ fontFamily: font, fontWeight: 500, fontSize: '12.5px', color: MUTED, margin: '0 0 20px' }}>
                            {currentAlbum.location} — {currentAlbum.date}
                        </p>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '16px' }}>
                            <div style={{ display: 'flex' }}>
                                <div style={{ width: '28px', height: '28px', overflow: 'hidden', flexShrink: 0, background: '#ddd', border: '2px solid #fafaf8' }}>
                                    {creatorAvatar && <img src={creatorAvatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                                </div>
                                {coAuthorAvatars.map(u => (
                                    <div key={u.username} style={{ width: '28px', height: '28px', overflow: 'hidden', flexShrink: 0, background: '#ddd', border: '2px solid #fafaf8', marginLeft: '-9px' }}>
                                        {u.avatar_url && <img src={u.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                                    </div>
                                ))}
                            </div>
                            <span
                                onClick={() => creatorUsername && navigate(`/creator/${creatorUsername}`)}
                                className={creatorUsername ? 'wlink' : ''}
                                style={{ fontFamily: font, fontSize: '13px', fontWeight: 700, color: INK, cursor: creatorUsername ? 'pointer' : 'default' }}
                            >
                                {currentAlbum.created_by}
                                {currentAlbum.co_authors?.length > 0 && ` +${currentAlbum.co_authors.length}`}
                            </span>
                        </div>

                        <button
                            onClick={handleCopyLink}
                            className="wlink"
                            style={{
                                background: 'none', border: 'none', color: INK,
                                fontSize: '12px', fontWeight: 500, cursor: 'pointer', fontFamily: font,
                                padding: 0, marginBottom: '24px',
                            }}
                        >
                            {copied ? 'Copié ✓' : 'Partager'}
                        </button>

                        {currentAlbum.description && (
                            <>
                                <div style={{ height: '1px', background: BORDER, margin: '0 0 20px' }} />
                                <p style={{ fontFamily: font, fontWeight: 500, fontSize: '13.5px', lineHeight: 1.7, color: '#555', margin: 0 }}>
                                    {currentAlbum.description}
                                </p>
                            </>
                        )}
                    </div>

                    {/* CONTENU — les photos, en vedette */}
                    <div className="album-content">

                        {currentAlbum.hero_url && (
                            <div style={{ width: '100%', height: isMobile ? '160px' : '260px', overflow: 'hidden', marginBottom: '24px' }}>
                                <img src={currentAlbum.hero_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                            </div>
                        )}

                        <p style={{ fontFamily: font, fontWeight: 500, fontSize: '11px', letterSpacing: '0.06em', color: '#bbb', marginBottom: '14px' }}>
                            {photos.length} photos
                        </p>

                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: '10px' }}>
                            {photos.map((photo, index) => (
                                <div
                                    key={index}
                                    onClick={() => { setLightboxIndex(index); setLightboxOpen(true); }}
                                    style={{ aspectRatio: '1 / 1', overflow: 'hidden', cursor: 'pointer' }}
                                >
                                    <img src={photo} alt={`photo-${index}`} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} onError={e => e.target.style.display = 'none'} />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Plus de ce créateur — pleine largeur, sous les deux colonnes */}
                {moreFromCreator.length > 0 && (
                    <div style={{ maxWidth: '1320px', margin: '64px auto 0', padding: '0 32px 72px' }}>
                        <p style={{ fontFamily: font, fontWeight: 500, fontSize: '11px', letterSpacing: '0.06em', color: '#bbb', marginBottom: '18px' }}>
                            More from {currentAlbum.created_by}
                        </p>
                        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(moreFromCreator.length, 4)}, 1fr)`, gap: '16px' }}>
                            {moreFromCreator.map(album => (
                                <div key={album.folder} onClick={() => navigate(`/region/${album.region}/${album.folder}`)} style={{ cursor: 'pointer' }}>
                                    <div style={{ width: '100%', aspectRatio: '1 / 1', overflow: 'hidden', marginBottom: '10px', background: '#ece8df' }}>
                                        {album.cover_url && <img src={album.cover_url} alt={album.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                                    </div>
                                    <p style={{ fontFamily: font, fontWeight: 700, fontSize: '13px', color: '#222', margin: '0 0 2px' }}>{album.title}</p>
                                    <p style={{ fontFamily: font, fontWeight: 500, fontSize: '10.5px', color: '#999', margin: 0 }}>{album.location} · {album.date}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <Footer variant="light" />

            <Lightbox open={lightboxOpen} close={() => setLightboxOpen(false)} index={lightboxIndex} slides={photos.map(photo => ({ src: photo }))} />
            <Lightbox
                open={coverLightbox}
                close={() => setCoverLightbox(false)}
                slides={[{ src: currentAlbum.cover_url }]}
                render={{ buttonPrev: () => null, buttonNext: () => null }}
            />
        </div>
    );
}

export default Album;